/**
 * Backend version helpers.
 *
 * `conversation.backend_api_version` is the image tag of the backend that
 * handled the call. CI produces three shapes:
 *
 *   - `v4.5.2`                  production release
 *   - `v4.5.3-rc.a55d6471`      release candidate (canary traffic split or LB override)
 *   - `v4.5.3-pr-1578-4fdf5a7`  PR preview environment
 *   - `v4.5.3-smoke.4fdf5a7`    smoke-test build on the dev environment
 *
 * Conversations from before the column existed have no version at all.
 */

export type BackendVersionKind = 'rc' | 'preview' | 'smoke'

/**
 * Substrings that identify each non-release kind. They double as filter
 * presets: the backend wraps string filters in `ILIKE '%...%'`, so sending
 * one of these as `backend_api_version` matches every tag of that kind.
 */
export const BACKEND_VERSION_FILTER_PRESETS: Record<BackendVersionKind, string> = {
  rc: '-rc.',
  preview: '-pr-',
  smoke: '-smoke.',
}

/** Classify a backend image tag; `null` for production releases and unknown versions. */
export function getBackendVersionKind(
  version: string | null | undefined
): BackendVersionKind | null {
  if (!version) return null
  if (version.includes(BACKEND_VERSION_FILTER_PRESETS.rc)) return 'rc'
  if (version.includes(BACKEND_VERSION_FILTER_PRESETS.preview)) return 'preview'
  if (version.includes(BACKEND_VERSION_FILTER_PRESETS.smoke)) return 'smoke'
  return null
}

/** PR number of a preview tag (`vX.Y.Z-pr-<n>-<sha>`); `null` for anything else. */
export function getPreviewPrNumber(version: string | null | undefined): number | null {
  const match = version?.match(/-pr-(\d+)-/)
  return match ? Number(match[1]) : null
}
